import React, { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Link, useParams, Navigate } from "react-router-dom";
import "@/App.css";
import { api as siteApi, resolveImageUrl, printCennik } from "@/lib/api";
import { Configurator } from "@/Configurator";
import {
  Phone, Mail, MapPin, Clock, ArrowUpRight, ArrowRight, Check,
  Factory, Shield, Truck, MessageCircle, ChevronDown, Menu, X,
  Hammer, Ruler, Layers, DoorOpen, Hexagon, Zap, Printer
} from "lucide-react";

const ICONS = { Layers, Hammer, Ruler, Zap, Factory, DoorOpen, Shield, MessageCircle, Truck };

/* ─────────────  SITE CONTEXT  ───────────── */
const SiteContext = createContext(null);
export const useSite = () => useContext(SiteContext);

/* ─────────────  DEFAULT (fallback while loading)  ───────────── */
const DEFAULT_COMPANY = {
  name: "Ogrodzenia Fajsławice",
  owner: "Jakub Stefaniak",
  nip: "5641799488",
  address: "Fajsławice 79C, 21-060 Fajsławice",
  region: "woj. lubelskie",
  email: "ogrodzeniafajslawice@gmail.com",
  phones: [
    { num: "785 405 785", name: "Jakub" },
    { num: "697 708 720", name: "Piotr" },
  ],
  hours: { weekdays: "Pon – Pt  9:00 – 17:00", saturday: "Sobota  9:00 – 14:00" },
  mapsUrl: "https://www.google.com/maps/place/Ogrodzenia+Fajs%C5%82awice",
  gps: "51.0980° N · 22.9519° E",
};

/* ─────────────  SITE PROVIDER  ───────────── */
const SiteProvider = ({ children }) => {
  const [data, setData] = useState({
    company: DEFAULT_COMPANY, hero: null, about: null, products: null,
    images: { logo: "", heroBg: "/fence-bg.jpg", aboutBg: "/about-bg.jpg" },
    pricing: { siatka: [], panele: [] }, gallery: [], configurator: {}, configuratorVariants: null, transport: null, productPages: {}, loading: true,
  });

  useEffect(() => {
    let alive = true;
    Promise.all([siteApi.getConfig(), siteApi.getPricing(), siteApi.getGallery()])
      .then(([cfg, pr, gal]) => {
        if (!alive) return;
        setData({
          company: cfg.company || DEFAULT_COMPANY,
          hero: cfg.hero || null,
          about: cfg.about || null,
          products: cfg.products || null,
          images: cfg.images || { logo: "", heroBg: "/fence-bg.jpg", aboutBg: "/about-bg.jpg" },
          pricing: pr || { siatka: [], panele: [] },
          gallery: Array.isArray(gal) ? gal : [],
          configurator: cfg.configurator || {},
          configuratorVariants: cfg.configuratorVariants || null,
          transport: cfg.transport || null,
          productPages: cfg.productPages || {},
          loading: false,
        });
      })
      .catch(() => alive && setData((d) => ({ ...d, loading: false })));
    return () => { alive = false; };
  }, []);

  return <SiteContext.Provider value={data}>{children}</SiteContext.Provider>;
};

/* ─────────────  PRICING (z arkuszy cennik & cennikdruk2)  ───────────── */

// Arkusz "cennik" — Ogrodzenia z siatki
const PRICING_SIATKA = [
  {
    name: "Siatka ogrodzeniowa 150 cm, 60×60",
    items: [
      { n: "Ocynk 2,5 mm", p: "14,00 zł/mb" },
      { n: "Ocynk 2,8 mm", p: "16,00 zł/mb" },
      { n: "Ocynk 3 mm",   p: "18,00 zł/mb" },
      { n: "PCV 2,5/3,6 (antracyt, czarny, brązowy, zielony)", p: "18,00 zł/mb" },
    ],
  },
  {
    name: "Siatka ogrodzeniowa 120 cm, 60×60",
    items: [
      { n: "Ocynk 2,5 mm", p: "11,50 zł/mb" },
      { n: "Ocynk 2,8 mm", p: "13,00 zł/mb" },
      { n: "Ocynk 3 mm",   p: "14,50 zł/mb" },
      { n: "PCV 2,5/3,6 (antracyt, czarny, brązowy, zielony)", p: "14,50 zł/mb" },
    ],
  },
  {
    name: "Siatka ogrodzeniowa 180 cm, 60×60",
    items: [
      { n: "Ocynk 2,5 mm", p: "17,00 zł/mb" },
      { n: "Ocynk 2,8 mm", p: "19,00 zł/mb" },
      { n: "Ocynk 3 mm",   p: "21,50 zł/mb" },
      { n: "PCV 2,5/3,6 (antracyt, czarny, brązowy, zielony)", p: "21,50 zł/mb" },
    ],
  },
  {
    name: "Siatka ogrodzeniowa 200 cm, 60×60",
    items: [
      { n: "Ocynk 2,5 mm", p: "18,50 zł/mb" },
      { n: "Ocynk 2,8 mm", p: "21,50 zł/mb" },
      { n: "Ocynk 3 mm",   p: "24,00 zł/mb" },
      { n: "PCV 2,5/3,6 (antracyt, czarny, brązowy, zielony)", p: "24,00 zł/mb" },
    ],
  },
  {
    name: "Słupki do siatki",
    items: [
      { n: "fi42,4 × 2 mm × 2 m — daszek, przelotki, podkład", p: "30 zł/szt" },
      { n: "fi42,4 × 2 mm × 2 m — nasadka, przelotki, podkład", p: "30 zł/szt" },
      { n: "Podpora słupka fi42,4 mm × 2 m, podkład", p: "38 zł/szt" },
      { n: "Ocynk fi42,4 × 2 mm × 2 m — nasadka, przelotki", p: "33 zł/szt" },
      { n: "Podpora słupka OC fi42 × 2 mm, 2 m", p: "41 zł/szt" },
    ],
  },
  {
    name: "Akcesoria do siatki",
    items: [
      { n: "Pręt startowy",                       p: "10 zł/szt" },
      { n: "Napinacz bębenkowy — komplet",        p: "7 zł/szt" },
      { n: "Śruba naciągowa M8 × 20 cm",          p: "4 zł/szt" },
      { n: "Śruba M8 z oczkiem",                  p: "2,50 zł/szt" },
      { n: "Przelotka z wkrętem RAL/OC",          p: "0,60 zł/szt" },
      { n: "Nasadka słupka podporowego",          p: "8 zł/szt" },
      { n: "Opaska fi42 / fi48",                  p: "1,50 zł/szt" },
    ],
  },
  {
    name: "Podmurówki betonowe 250 × 25 cm",
    items: [
      { n: "Kaseton — gładka / cegiełka / piaskowiec / deska",          p: "40 zł/szt" },
      { n: "Grafit, kaseton — gładka / cegiełka / piaskowiec / deska", p: "47 zł/szt" },
      { n: "Modern / palisada",                                  p: "54 zł/szt" },
      { n: "Modern / palisada — grafit",                         p: "60 zł/szt" },
      { n: "Gładka dwustronna",                                  p: "52 zł/szt" },
      { n: "Gładka dwustronna — grafit",                         p: "58 zł/szt" },
    ],
  },
  {
    name: "Łączniki podmurówek",
    items: [
      { n: "Łącznik stalowy ceownik RAL 25 cm", p: "7 zł/szt" },
      { n: "Łącznik betonowy",                  p: "14 zł/szt" },
    ],
  },
  {
    name: "Słupki betonowe",
    items: [
      { n: "h — 200 cm", p: "25 zł/szt" },
      { n: "h — 220 cm", p: "27 zł/szt" },
      { n: "h — 240 cm", p: "29 zł/szt" },
      { n: "h — 260 cm", p: "31 zł/szt" },
      { n: "h — 280 cm", p: "34 zł/szt" },
    ],
  },
  {
    name: "Siatka leśna",
    items: [
      { n: "150 / 15 / 30 G6 (50 mb)", p: "180 zł/rolka" },
    ],
  },
];

// Arkusz "cennikdruk2" — Ogrodzenia panelowe
const PRICING_PANELE = [
  {
    name: "Panele 3D fi4 RAL",
    items: [
      { n: "Panel 103 cm fi4", p: "62 zł/szt" },
      { n: "Panel 123 cm fi4", p: "64 zł/szt" },
      { n: "Panel 133 cm fi4", p: "69 zł/szt" },
      { n: "Panel 153 cm fi4", p: "74 zł/szt" },
      { n: "Panel 173 cm fi4", p: "82 zł/szt" },
      { n: "Panel 203 cm fi4", p: "98 zł/szt" },
    ],
  },
  {
    name: "Panele 3D fi5 RAL",
    items: [
      { n: "Panel 103 cm fi5", p: "87 zł/szt" },
      { n: "Panel 123 cm fi5", p: "89 zł/szt" },
      { n: "Panel 133 cm fi5", p: "98 zł/szt" },
      { n: "Panel 153 cm fi5", p: "103 zł/szt" },
      { n: "Panel 173 cm fi5", p: "119 zł/szt" },
      { n: "Panel 203 cm fi5", p: "140 zł/szt" },
    ],
  },
  {
    name: "Panele 3D fi4 OCYNK",
    items: [
      { n: "Panel 103 cm fi4", p: "74 zł/szt" },
      { n: "Panel 123 cm fi4", p: "78 zł/szt" },
      { n: "Panel 133 cm fi4", p: "86 zł/szt" },
      { n: "Panel 153 cm fi4", p: "89 zł/szt" },
      { n: "Panel 173 cm fi4", p: "102 zł/szt" },
      { n: "Panel 203 cm fi4", p: "114 zł/szt" },
    ],
  },
  {
    name: "Panele 3D fi5 OCYNK",
    items: [
      { n: "Panel 103 cm fi5", p: "98 zł/szt" },
      { n: "Panel 123 cm fi5", p: "109 zł/szt" },
      { n: "Panel 133 cm fi5", p: "125 zł/szt" },
      { n: "Panel 153 cm fi5", p: "129 zł/szt" },
      { n: "Panel 173 cm fi5", p: "143 zł/szt" },
      { n: "Panel 203 cm fi5", p: "162 zł/szt" },
    ],
  },
  {
    name: "Słupki panelowe 60×40 / 1,25 mm",
    items: [
      { n: "RAL 60×40, 2 m",   p: "35 zł/szt" },
      { n: "RAL 60×40, 2,2 m", p: "37 zł/szt" },
      { n: "RAL 60×40, 2,4 m", p: "40 zł/szt" },
      { n: "RAL 60×40, 2,6 m", p: "42 zł/szt" },
      { n: "RAL 60×40, 2,8 m", p: "44 zł/szt" },
    ],
  },
  {
    name: "Słupki panelowe 60×40 / 1,5 mm",
    items: [
      { n: "RAL 60×40, 2 m",   p: "43 zł/szt" },
      { n: "RAL 60×40, 2,2 m", p: "47 zł/szt" },
      { n: "RAL 60×40, 2,4 m", p: "50 zł/szt" },
      { n: "RAL 60×40, 2,6 m", p: "53 zł/szt" },
      { n: "RAL 60×40, 2,8 m", p: "56 zł/szt" },
    ],
  },
  {
    name: "Słupki panelowe 60×40 / 2 mm",
    items: [
      { n: "RAL 60×40, 2 m",   p: "48 zł/szt" },
      { n: "RAL 60×40, 2,2 m", p: "52 zł/szt" },
      { n: "RAL 60×40, 2,4 m", p: "56 zł/szt" },
      { n: "RAL 60×40, 2,6 m", p: "60 zł/szt" },
      { n: "RAL 60×40, 2,8 m", p: "63 zł/szt" },
    ],
  },
  {
    name: "Obejmy panelowe — komplet RAL",
    items: [
      { n: "Obejma przelotowa 60×40 — komplet", p: "4 zł/kpl" },
      { n: "Obejma końcowa 60×40 — komplet",    p: "4 zł/kpl" },
      { n: "Obejma narożna 60×40 — komplet",    p: "4 zł/kpl" },
      { n: "Obejma 80×80 — komplet",            p: "8 zł/kpl" },
      { n: "Uchwyt uniwersalny",                p: "3 zł/szt" },
    ],
  },
];

/* ─────────────  IMAGE ASSETS  ───────────── */
const IMG = {
  hero:    "https://images.pexels.com/photos/33903903/pexels-photo-33903903.jpeg?auto=compress&w=1600",
  factory: "https://images.pexels.com/photos/35378172/pexels-photo-35378172.jpeg?auto=compress&w=1200",
  panel:   "https://images.pexels.com/photos/33903903/pexels-photo-33903903.jpeg?auto=compress&w=900",
  siatka:  "https://images.unsplash.com/photo-1771582975652-916bd051a66a?crop=entropy&cs=srgb&fm=jpg&w=900&q=85",
  slupki:  "https://images.pexels.com/photos/8902582/pexels-photo-8902582.jpeg?auto=compress&w=900",
  akces:   "https://images.pexels.com/photos/17372998/pexels-photo-17372998.jpeg?auto=compress&w=900",
  brama:   "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?auto=format&fit=crop&w=900&q=80",
};

/* ─────────────  LOGO  ───────────── */
const Logo = ({ size = 64 }) => {
  const ctx = useContext(SiteContext);
  const customLogo = ctx?.images?.logo;
  const logoSrc = customLogo ? resolveImageUrl(customLogo) : "/logo.png";
  return (
    <div className="flex items-center gap-3" data-testid="brand-logo">
      <img
        src={logoSrc}
        alt="Ogrodzenia Fajsławice"
        style={{ height: size, width: "auto" }}
        className="object-contain"
      />
      <div className="leading-none">
        <div className="font-display text-white text-2xl tracking-[0.18em] uppercase">Ogrodzenia</div>
        <div className="font-mono text-[#ea580c] text-xs tracking-[0.35em] mt-1.5 uppercase">Fajsławice</div>
      </div>
    </div>
  );
};

/* ─────────────  NAV  ───────────── */
const Nav = () => {
  const { company: COMPANY } = useSite();
  const [open, setOpen] = useState(false);
  const links = [
    ["O firmie", "#o-nas"],
    ["Produkty", "#produkty"],
    ["Galeria",  "#galeria"],
    ["Cennik",   "#cennik"],
    ["Konfigurator", "#konfigurator"],
    ["Lokalizacja", "#lokalizacja"],
    ["Transport", "#transport"],
    ["Kontakt",  "#kontakt"],
  ];
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-[#0a0a0b]/80 border-b border-[#2a2a2e]" data-testid="main-nav">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
        <a href="#top"><Logo /></a>
        <nav className="hidden lg:flex items-center gap-8">
          {links.map(([label, href]) => (
            <a key={href} href={href} className="text-sm text-[#a8a29e] hover:text-[#ea580c] transition-colors font-medium" data-testid={`nav-${label.toLowerCase()}`}>
              {label}
            </a>
          ))}
        </nav>
        <a href={`tel:+48${COMPANY.phones[0].num.replace(/\s/g,'')}`} className="!hidden lg:!inline-flex btn-primary" data-testid="nav-call-btn-1">
          <Phone size={16}/> {COMPANY.phones[0].num}
        </a>
        <a href={`tel:+48${COMPANY.phones[1].num.replace(/\s/g,'')}`} className="!hidden lg:!inline-flex btn-ghost" data-testid="nav-call-btn-2">
          <Phone size={16}/> {COMPANY.phones[1].num}
        </a>
        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X size={28}/> : <Menu size={28}/>}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#2a2a2e] bg-[#0a0a0b]" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map(([label, href]) => (
              <a key={href} href={href} onClick={() => setOpen(false)} className="text-base text-[#a8a29e] hover:text-[#ea580c] py-2" data-testid={`mobile-nav-${label.toLowerCase()}`}>
                {label}
              </a>
            ))}
            <a href={`tel:+48${COMPANY.phones[0].num.replace(/\s/g,'')}`} className="btn-primary mt-2 justify-center" data-testid="mobile-call-btn-1">
              <Phone size={16}/> {COMPANY.phones[0].num} · {COMPANY.phones[0].name}
            </a>
            <a href={`tel:+48${COMPANY.phones[1].num.replace(/\s/g,'')}`} className="btn-ghost justify-center" data-testid="mobile-call-btn-2">
              <Phone size={16}/> {COMPANY.phones[1].num} · {COMPANY.phones[1].name}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

/* ─────────────  HERO  ───────────── */
const Hero = () => {
  const { hero, images } = useSite();
  const h = hero || {};
  const heroBg = resolveImageUrl(images?.heroBg || "/fence-bg.jpg");
  const stats = h.stats || [
    { k: "10+ lat", sub: "Doświadczenie", desc: "Produkujemy siatkę ogrodzeniową od ponad 10 lat." },
    { k: "Jakość", sub: "Standard", desc: "Stawiamy na trwałość materiałów i zadowolenie klientów." },
    { k: "Transport", sub: "Lubelskie", desc: "Dostarczamy bezpośrednio na plac budowy." },
    { k: "Doradztwo", sub: "Indywidualne", desc: "Dobierzemy system i materiał idealny dla Twojej działki." },
  ];
  const marquee = h.marquee || ["Siatka ogrodzeniowa", "Panele 3D", "Akcesoria", "Transport"];
  const t2Lines = (h.title_2 || "Ogrodzenia\nPanelowe").split("\n");
  return (
  <section id="top" className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden" data-testid="hero-section">
    {/* Tło hero — siatka ogrodzeniowa */}
    <img src={heroBg} alt="" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover object-right" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/60 to-transparent" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-transparent" />

    <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-8 w-full">
      <div className="lg:col-span-8 fade-up">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-6 sm:mb-8">
          <span className="tag tag-orange">{h.tag || "// PRODUCENT SIATKI OGRODZENIOWEJ"}</span>
          <span className="tag">{h.tag2 || "FAJSŁAWICE · LUBELSKIE"}</span>
        </div>
        <h1 className="font-display text-[11vw] sm:text-[10vw] lg:text-[10rem] leading-[0.85] tracking-tight uppercase mb-6 break-words">
          <span className="block text-[#ea580c]">{h.title_1 || "Siatka"}</span>
          <span className="block text-white">{t2Lines.map((l, i) => <React.Fragment key={i}>{i > 0 && <><span className="lg:hidden">&nbsp;</span><br className="hidden lg:block"/></>}{l}</React.Fragment>)}</span>
          <span className="block text-stroke">{h.title_3 || "Akcesoria"}</span>
        </h1>
        <p className="text-base sm:text-lg lg:text-xl text-[#a8a29e] max-w-2xl mb-8 sm:mb-10 leading-relaxed">
          {h.description || "Produkujemy siatkę oraz kompletujemy i dostarczamy systemy ogrodzeniowe panelowe."}
        </p>
        <div className="flex flex-wrap gap-3 sm:gap-4">
          <a href="#produkty" className="btn-primary" data-testid="hero-products-btn">Zobacz produkty <ArrowRight size={16}/></a>
          <a href="#cennik" className="btn-ghost" data-testid="hero-pricing-btn">Sprawdź cennik <ArrowUpRight size={16}/></a>
        </div>
      </div>

      <div className="lg:col-span-4 grid grid-cols-2 gap-2 sm:gap-3 self-end mt-2">
        {stats.map((s, i) => (
          <div key={i} className="card p-3 sm:p-4 lg:p-5" data-testid={`hero-stat-${i}`}>
            <div className="font-display text-xl sm:text-2xl lg:text-3xl text-[#ea580c] leading-none">{s.k}</div>
            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-[#ea580c]/70 mt-1.5 sm:mt-2 font-mono">{s.sub}</div>
            <div className="text-[11px] sm:text-xs text-[#a8a29e] mt-2 sm:mt-3 leading-snug">{s.desc}</div>
          </div>
        ))}
      </div>
    </div>

    {/* marquee — produkty z cennika */}
    <div className="absolute bottom-0 left-0 right-0 border-t border-[#2a2a2e] bg-[#0a0a0b]/80 backdrop-blur-sm py-4">
      <div className="marquee">
        {[...Array(2)].map((_, idx) => (
          <div key={idx} className="marquee-track font-mono text-xs uppercase text-[#a8a29e] tracking-[0.2em]">
            {marquee.map((t,i) => (
              <span key={i} className="flex items-center gap-3 shrink-0"><span className="text-[#ea580c]">●</span>{t}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

/* ─────────────  ABOUT  ───────────── */
const About = () => {
  const { about, images } = useSite();
  const a = about || {};
  const aboutBg = resolveImageUrl(images?.aboutBg || "/about-bg.jpg");
  const paragraphs = a.paragraphs || [];
  const features = a.features || [];
  const FEATURE_ICONS = [Factory, Shield, MessageCircle, Truck];
  return (
  <section id="o-nas" className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 overflow-hidden" data-testid="about-section">
    {/* Tło — panele ogrodzeniowe */}
    <img src={aboutBg} alt="" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover object-right" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-[#0a0a0b]/85 to-[#0a0a0b]/40" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-[#0a0a0b]/60" />
    <div className="relative max-w-[1400px] mx-auto">
      <div className="flex items-center gap-4 mb-6 sm:mb-8">
        <span className="font-mono text-xs text-[#a8a29e]">[ 01 ]</span>
        <div className="h-px w-16 bg-[#ea580c]" />
        <span className="font-mono text-xs text-[#ea580c] uppercase tracking-[0.2em]">O firmie</span>
      </div>

      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-8xl uppercase leading-[0.95] sm:leading-[0.9] mb-6 sm:mb-8">
            {a.title_1 || "Produkujemy"} <span className="text-[#ea580c]">{a.title_2 || "jakość"}</span><br/>
            <span className="text-[#ea580c]">{a.title_3 || "od ponad 10 lat"}</span>.
          </h2>
          <div className="space-y-5 sm:space-y-6 text-[#d6d3d1] text-base sm:text-lg leading-relaxed max-w-3xl">
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-12 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2a2a2e] border border-[#2a2a2e]">
        {features.map((f, i) => {
          const Icon = FEATURE_ICONS[i] || Factory;
          return (
            <div key={i} className="bg-[#0a0a0b] p-5 sm:p-6 lg:p-8" data-testid={`about-feature-${i}`}>
              <Icon className="text-[#ea580c] mb-3 sm:mb-4" size={24} strokeWidth={2}/>
              <h4 className="font-display text-base sm:text-xl lg:text-2xl uppercase mb-2 sm:mb-3 text-white leading-tight">{f.title}</h4>
              <p className="text-xs sm:text-sm text-[#a8a29e] leading-relaxed">{f.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
  );
};

/* ─────────────  TRANSPORT  ───────────── */
const Transport = () => {
  const { transport, settings } = useSite();
  const T = {
    heading: transport?.heading || "Transport",
    subheading: transport?.subheading || "Obszar dostaw — promień 100 km od Fajsławic",
    body: transport?.body || "",
    ctaText: transport?.ctaText || "Zadzwoń",
    ctaPhone: transport?.ctaPhone || (settings?.phones?.[0]?.num) || "785 405 785",
    ctaPhone2: transport?.ctaPhone2 || (settings?.phones?.[1]?.num) || "",
    cities: (transport?.cities && transport.cities.length) ? transport.cities : [],
  };

  // Schema.org Service z areaServed — dla SEO, niewidoczne dla użytkownika.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Dostawa ogrodzeń panelowych i siatki",
    "provider": {
      "@type": "LocalBusiness",
      "name": settings?.companyName || "Ogrodzenia Fajsławice",
      "address": { "@type": "PostalAddress", "addressLocality": "Fajsławice", "addressRegion": "PL-LU", "postalCode": "21-060", "addressCountry": "PL" },
    },
    "areaServed": T.cities.map((c) => ({ "@type": "City", "name": c })),
  };

  // Pozycje miast na uproszczonej mapie (SVG viewBox 600x600). Centrum = Fajsławice (300,300).
  // Wyliczone "na oko" zgodnie z kierunkami świata: Lublin NW, Krasnystaw SE, Chełm E, Zamość SE, etc.
  const cityPositions = {
    "Lublin":         { x: 215, y: 235, dir: "NW" },
    "Krasnystaw":     { x: 332, y: 327, dir: "SE" },
    "Świdnik":        { x: 240, y: 250, dir: "NW" },
    "Chełm":          { x: 408, y: 287, dir: "E"  },
    "Zamość":         { x: 388, y: 392, dir: "SE" },
    "Łęczna":         { x: 320, y: 248, dir: "NE" },
    "Lubartów":       { x: 232, y: 192, dir: "N"  },
    "Biłgoraj":       { x: 250, y: 460, dir: "S"  },
    "Hrubieszów":     { x: 470, y: 360, dir: "E"  },
    "Tomaszów Lub.":  { x: 410, y: 450, dir: "SE" },
    "Puławy":         { x: 165, y: 245, dir: "NW" },
  };

  return (
    <section id="transport" className="relative py-20 sm:py-32 bg-[#0a0a0b] overflow-hidden">
      {/* tło: subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(234,88,12,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(234,88,12,0.5) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* TEXT */}
          <div data-testid="transport-text">
            <div className="inline-block mb-4 text-[#ea580c] font-mono text-xs uppercase tracking-[0.3em] border border-[#ea580c]/30 px-3 py-1">
              // {T.subheading.split("—")[0]?.trim() || "Logistyka"}
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.95] uppercase tracking-tight mb-6">
              {T.heading}
            </h2>
            <p className="text-base sm:text-lg text-[#d6d3d1] leading-relaxed mb-4">
              {T.subheading}
            </p>
            {T.body.split("\n\n").map((para, i) => (
              <p key={i} className="text-sm sm:text-base text-[#a8a29e] leading-relaxed mb-4">
                {para}
              </p>
            ))}

            <div className="flex flex-wrap gap-3 mt-4">
              <a
                href={`tel:+48${(T.ctaPhone || "").replace(/\s+/g, "")}`}
                data-testid="transport-cta"
                className="inline-flex items-center gap-3 px-6 py-3 bg-[#ea580c] hover:bg-[#c2410c] text-white font-mono text-sm uppercase tracking-widest transition-colors"
              >
                {T.ctaText}: {T.ctaPhone}
              </a>
              {T.ctaPhone2 && (
                <a
                  href={`tel:+48${(T.ctaPhone2 || "").replace(/\s+/g, "")}`}
                  data-testid="transport-cta-2"
                  className="inline-flex items-center gap-3 px-6 py-3 bg-transparent border border-[#2a2a2e] hover:border-[#ea580c] text-[#d6d3d1] hover:text-[#ea580c] font-mono text-sm uppercase tracking-widest transition-colors"
                >
                  {T.ctaText}: {T.ctaPhone2}
                </a>
              )}
            </div>
          </div>

          {/* SVG MAP */}
          <div className="relative">
            <svg
              viewBox="0 0 600 600"
              className="w-full h-auto"
              data-testid="transport-map"
              aria-label="Mapa obszaru dostaw"
            >
              <defs>
                <radialGradient id="t-glow" cx="0.5" cy="0.5" r="0.5">
                  <stop offset="0%" stopColor="#ea580c" stopOpacity="0.35" />
                  <stop offset="60%" stopColor="#ea580c" stopOpacity="0.08" />
                  <stop offset="100%" stopColor="#ea580c" stopOpacity="0" />
                </radialGradient>
                <pattern id="t-dots" width="14" height="14" patternUnits="userSpaceOnUse">
                  <circle cx="7" cy="7" r="0.6" fill="#a8a29e" opacity="0.25" />
                </pattern>
              </defs>

              {/* background dots */}
              <rect width="600" height="600" fill="url(#t-dots)" />

              {/* outer circle — 100 km */}
              <circle cx="300" cy="300" r="230" fill="url(#t-glow)" />
              <circle
                cx="300" cy="300" r="230"
                fill="none" stroke="#ea580c" strokeWidth="1.4"
                strokeDasharray="6 5" opacity="0.7"
              />
              {/* inner circle — 50 km */}
              <circle
                cx="300" cy="300" r="120"
                fill="none" stroke="#ea580c" strokeWidth="1" opacity="0.5"
                strokeDasharray="3 4"
              />

              {/* labels promieni */}
              <text x="300" y="80" textAnchor="middle" fill="#ea580c" fontSize="11" fontFamily="monospace" letterSpacing="2">~100 KM</text>
              <text x="300" y="190" textAnchor="middle" fill="#ea580c" opacity="0.6" fontSize="9" fontFamily="monospace" letterSpacing="2">~50 KM</text>

              {/* centrum — Fajsławice */}
              <g>
                <circle cx="300" cy="300" r="22" fill="#ea580c" opacity="0.15">
                  <animate attributeName="r" values="22;30;22" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.15;0;0.15" dur="3s" repeatCount="indefinite" />
                </circle>
                <circle cx="300" cy="300" r="8" fill="#ea580c" />
                <circle cx="300" cy="300" r="3" fill="#fff" />
                <text x="300" y="335" textAnchor="middle" fill="#fff" fontSize="13" fontFamily="'Bebas Neue', Impact, sans-serif" letterSpacing="2">FAJSŁAWICE</text>
              </g>

              {/* MIASTA — tylko te wybrane przez admina */}
              {T.cities.filter((c) => cityPositions[c]).map((c) => {
                const p = cityPositions[c];
                const isLeft = p.x < 300;
                return (
                  <g key={c}>
                    <line x1="300" y1="300" x2={p.x} y2={p.y} stroke="#fff" strokeWidth="0.5" opacity="0.18" />
                    <circle cx={p.x} cy={p.y} r="3.2" fill="#fff" />
                    <circle cx={p.x} cy={p.y} r="1.4" fill="#ea580c" />
                    <text
                      x={isLeft ? p.x - 8 : p.x + 8}
                      y={p.y + 4}
                      textAnchor={isLeft ? "end" : "start"}
                      fill="#d6d3d1"
                      fontSize="11"
                      fontFamily="'Bebas Neue', Impact, sans-serif"
                      letterSpacing="1.5"
                    >
                      {c.toUpperCase()}
                    </text>
                  </g>
                );
              })}
            </svg>

            {/* chipy miast pod mapą (mobile-friendly fallback + drugie wystąpienie nazw dla Google) */}
            <div className="mt-4 flex flex-wrap gap-2 justify-center" data-testid="transport-chips">
              {T.cities.map((c, i) => (
                <span
                  key={i}
                  className="text-[10px] sm:text-xs font-mono uppercase tracking-widest text-[#a8a29e] border border-[#2a2a2e] px-2.5 py-1 hover:border-[#ea580c] hover:text-[#ea580c] transition-colors"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </section>
  );
};

/* ─────────────  PRODUCTS  ───────────── */
/* ─── Mapowanie tytułów produktów na sluga podstrony ─── */
const PRODUCT_SLUG_MAP = [
  { test: (t) => /siatka.*ocynk/i.test(t), slug: "siatka-ocynkowana" },
  { test: (t) => /siatka.*pow[lł]ek/i.test(t) || /siatka.*pcv/i.test(t), slug: "siatka-powlekana" },
  { test: (t) => /panel.*fi\s?4/i.test(t) || /fi\s?4.*panel/i.test(t), slug: "panele-3d-fi4" },
  { test: (t) => /panel.*fi\s?5/i.test(t) || /fi\s?5.*panel/i.test(t), slug: "panele-3d-fi5" },
  { test: (t) => /s[lł]upk.*panel/i.test(t), slug: "slupki-panelowe" },
  { test: (t) => /^podmur/i.test(t) || /podmur[oó]wk/i.test(t), slug: "podmurowki-betonowe" },
];
const productToSlug = (title) => {
  const match = PRODUCT_SLUG_MAP.find((m) => m.test(title || ""));
  return match ? match.slug : null;
};

const Products = () => {
  const { products } = useSite();
  const items = (products || []).map((p, i) => ({
    num: String(i + 1).padStart(2, "0"),
    icon: ICONS[p.icon] || Layers,
    title: p.title,
    blurb: p.blurb,
    image: p.image || "",
  }));

  return (
    <section id="produkty" className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-[#0d0d0f]" data-testid="products-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-6 sm:mb-8">
          <span className="font-mono text-xs text-[#a8a29e]">[ 02 ]</span>
          <div className="h-px w-16 bg-[#ea580c]" />
          <span className="font-mono text-xs text-[#ea580c] uppercase tracking-[0.2em]">Asortyment</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-end mb-10 sm:mb-16">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-8xl uppercase leading-[0.95] sm:leading-[0.9]">
            Oferowane przez nas <span className="text-[#ea580c]">produkty</span>.
          </h2>
          <p className="text-base sm:text-lg text-[#a8a29e] max-w-xl">
            Od pojedynczej siatki po kompletny system panelowy z bramą. W jednym miejscu znajdziesz wszystkie elementy potrzebne do zbudowania trwałego ogrodzenia.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2e] border border-[#2a2a2e]">
          {items.map((p, i) => {
            const slug = productToSlug(p.title);
            const inner = (
              <>
                {p.image && (
                  <div className="relative aspect-[4/3] overflow-hidden hidden sm:block">
                    <img src={resolveImageUrl(p.image)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b]/90 via-transparent to-transparent" />
                  </div>
                )}
                <div className="p-3.5 sm:p-5 lg:p-7">
                  <div className="flex items-start justify-between mb-3 sm:mb-5">
                    <div className="w-9 h-9 sm:w-12 sm:h-12 bg-[#ea580c]/10 border border-[#ea580c]/30 flex items-center justify-center group-hover:bg-[#ea580c] transition-colors">
                      <p.icon className="text-[#ea580c] group-hover:text-white transition-colors w-4 h-4 sm:w-[22px] sm:h-[22px]"/>
                    </div>
                    <span className="font-mono text-[10px] sm:text-xs text-[#57534e] tracking-widest">// {p.num}</span>
                  </div>
                  <h3 className="font-display text-sm sm:text-lg lg:text-xl uppercase text-white mb-2 sm:mb-3 leading-tight">{p.title}</h3>
                  <p className="text-[11px] sm:text-sm text-[#a8a29e] leading-snug sm:leading-relaxed">{p.blurb}</p>
                  {slug && (
                    <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest text-[#ea580c] group-hover:translate-x-1 transition-transform">
                      Zobacz szczegóły <ArrowRight size={12}/>
                    </span>
                  )}
                </div>
              </>
            );
            const cls = "bg-[#0a0a0b] group transition-colors relative overflow-hidden block " +
              (slug ? "hover:bg-[#131316] cursor-pointer" : "opacity-70 cursor-default");
            return slug ? (
              <Link key={i} to={`/produkty/${slug}`} className={cls} data-testid={`product-card-${i}`}>
                {inner}
              </Link>
            ) : (
              <article key={i} className={cls} data-testid={`product-card-${i}`}>{inner}</article>
            );
          })}
        </div>

        <div className="mt-10 sm:mt-16 card p-6 sm:p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-5 lg:gap-6" data-testid="products-cta">
          <div>
            <h3 className="font-display text-2xl sm:text-3xl uppercase text-white">Potrzebujesz wyceny?</h3>
            <p className="text-sm sm:text-base text-[#a8a29e] mt-2">Zadzwoń — doradzimy i przygotujemy ofertę zwykle w tym samym dniu.</p>
          </div>
          <a href="#kontakt" className="btn-primary shrink-0" data-testid="products-cta-btn">Zapytaj o ofertę <ArrowRight size={16}/></a>
        </div>
      </div>
    </section>
  );
};

/* ─────────────  CENNIK  ───────────── */
const PriceCategory = ({ category }) => (
  <div className="card overflow-hidden" data-testid={`price-cat-${category.name.toLowerCase().replace(/\s/g,'-')}`}>
    <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-[#2a2a2e] bg-[#131316]">
      <h3 className="font-display text-base sm:text-xl uppercase text-white tracking-tight leading-tight">{category.name}</h3>
    </div>
    <div className="divide-y divide-[#2a2a2e]">
      {category.items.map((item, i) => (
        <div key={i} className="px-4 sm:px-6 py-3 grid grid-cols-12 gap-3 sm:gap-4 items-center hover:bg-[#131316]/50 transition-colors">
          <div className="col-span-7 lg:col-span-8 text-[#d6d3d1] text-xs sm:text-sm leading-snug">{item.n}</div>
          <div className="col-span-5 lg:col-span-4 text-right">
            <span className="font-display text-base sm:text-lg text-[#ea580c] whitespace-nowrap">{item.p}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Cennik = () => {
  const { pricing, company } = useSite();
  const [tab, setTab] = useState("siatka");
  const data = tab === "siatka" ? (pricing.siatka || []) : (pricing.panele || []);

  return (
    <section id="cennik" className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12" data-testid="cennik-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-6 sm:mb-8">
          <span className="font-mono text-xs text-[#a8a29e]">[ 04 ]</span>
          <div className="h-px w-16 bg-[#ea580c]" />
          <span className="font-mono text-xs text-[#ea580c] uppercase tracking-[0.2em]">Cennik</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-end mb-8 sm:mb-12">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-8xl uppercase leading-[0.95] sm:leading-[0.9]">
            Nasze aktualne <span className="text-[#ea580c]">ceny</span>:
          </h2>
          <div className="flex flex-col gap-3 sm:gap-4 max-w-xl">
            <p className="text-base sm:text-lg text-[#a8a29e]">
              Wybierz typ ogrodzenia, by zobaczyć aktualny cennik. Pełen cennik (kolory, niestandardowe wysokości, transport) — na zapytanie telefoniczne lub mailowe.
            </p>
            <button
              onClick={() => { siteApi.trackDownload(); printCennik(pricing, company); }}
              className="btn-primary self-start"
              data-testid="print-cennik-btn"
            >
              <Printer size={16}/> Drukuj cennik (PDF / A4)
            </button>
          </div>
        </div>

        {/* Zakładki */}
        <div className="flex flex-col sm:flex-row gap-px bg-[#2a2a2e] border border-[#2a2a2e] mb-8 sm:mb-10" data-testid="cennik-tabs">
          <button
            onClick={() => setTab("siatka")}
            className={`flex-1 px-5 sm:px-8 py-4 sm:py-6 text-left transition-colors ${tab === "siatka" ? "bg-[#ea580c] text-white" : "bg-[#0a0a0b] text-[#d6d3d1] hover:bg-[#131316]"}`}
            data-testid="tab-siatka"
          >
            <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-70 mb-1">// Kategoria 01</div>
            <div className="font-display text-xl sm:text-2xl lg:text-3xl uppercase leading-tight">Ogrodzenia z siatki</div>
            <div className={`text-xs sm:text-sm mt-1.5 sm:mt-2 ${tab === "siatka" ? "text-white/80" : "text-[#a8a29e]"}`}>Siatka, słupki, akcesoria, podmurówki, słupki betonowe, siatka leśna</div>
          </button>
          <button
            onClick={() => setTab("panele")}
            className={`flex-1 px-5 sm:px-8 py-4 sm:py-6 text-left transition-colors ${tab === "panele" ? "bg-[#ea580c] text-white" : "bg-[#0a0a0b] text-[#d6d3d1] hover:bg-[#131316]"}`}
            data-testid="tab-panele"
          >
            <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] opacity-70 mb-1">// Kategoria 02</div>
            <div className="font-display text-xl sm:text-2xl lg:text-3xl uppercase leading-tight">Ogrodzenia panelowe</div>
            <div className={`text-xs sm:text-sm mt-1.5 sm:mt-2 ${tab === "panele" ? "text-white/80" : "text-[#a8a29e]"}`}>Panele 3D fi4 / fi5 RAL i ocynk, słupki panelowe, obejmy</div>
          </button>
        </div>

        {/* Tabela podkategorii */}
        <div className="grid lg:grid-cols-2 gap-6" data-testid={`cennik-table-${tab}`}>
          {data.map((cat, i) => (
            <PriceCategory key={`${tab}-${i}`} category={cat} />
          ))}
        </div>

        <div className="mt-12 p-6 border border-[#2a2a2e] bg-[#131316] flex flex-col lg:flex-row gap-4 items-start lg:items-center" data-testid="cennik-disclaimer">
          <span className="tag tag-orange shrink-0">UWAGA</span>
          <p className="text-sm text-[#a8a29e]">
            Ceny mogą ulec zmianie w&nbsp;zależności od&nbsp;ilości zamówienia, koloru i&nbsp;aktualnych cen surowców. Po szczegółową, indywidualną wycenę zapraszamy do&nbsp;kontaktu telefonicznego — <a href="tel:+48785405785" className="text-[#ea580c] hover:underline">785 405 785</a>.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ─────────────  LOCATION  ───────────── */
const Location = () => {
  const { company: COMPANY } = useSite();
  return (
  <section id="lokalizacja" className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-[#0d0d0f]" data-testid="location-section">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex items-center gap-4 mb-6 sm:mb-8">
        <span className="font-mono text-xs text-[#a8a29e]">[ 06 ]</span>
        <div className="h-px w-16 bg-[#ea580c]" />
        <span className="font-mono text-xs text-[#ea580c] uppercase tracking-[0.2em]">Lokalizacja</span>
      </div>

      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-5">
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl uppercase leading-[0.95] sm:leading-[0.9] mb-6 sm:mb-8">
            Znajdziesz<br/>nas w<br/><span className="text-[#ea580c]">Fajsławicach</span>.
          </h2>
          <p className="text-base sm:text-lg text-[#a8a29e] mb-6 sm:mb-8 max-w-md">
            Odwiedź nas osobiście i zobacz naszą produkcję. Chętnie pokażemy wzory siatek, paneli i podmurówek.
          </p>

          <div className="space-y-4 sm:space-y-5">
            <div className="border-l-2 border-[#ea580c] pl-4 sm:pl-5" data-testid="location-address">
              <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c]">Adres</div>
              <div className="text-white text-lg sm:text-xl mt-1">{COMPANY.address}</div>
              <div className="text-sm sm:text-base text-[#a8a29e]">{COMPANY.region}</div>
            </div>
            <div className="border-l-2 border-[#ea580c] pl-4 sm:pl-5" data-testid="location-gps">
              <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c]">GPS</div>
              <div className="text-white text-base sm:text-xl mt-1 font-mono">51.0980° N · 22.9519° E</div>
            </div>
          </div>

          <a href={COMPANY.mapsUrl} target="_blank" rel="noopener noreferrer" className="btn-primary mt-6 sm:mt-8" data-testid="open-maps-btn">
            <MapPin size={16}/> Otwórz w Google Maps <ArrowUpRight size={14}/>
          </a>
        </div>

        <div className="lg:col-span-7 relative aspect-[4/3] lg:aspect-auto border border-[#2a2a2e] overflow-hidden min-h-[320px] sm:min-h-[400px] lg:min-h-[500px]">
          <iframe
            title="Mapa — Ogrodzenia Fajsławice"
            src="https://maps.google.com/maps?q=Ogrodzenia+Fajs%C5%82awice&ll=51.0979883,22.9519093&z=16&output=embed"
            className="absolute inset-0 w-full h-full"
            style={{ filter: "invert(0.92) hue-rotate(180deg) saturate(0.6) brightness(0.95)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            data-testid="location-map"
          />
        </div>
      </div>
    </div>
  </section>
  );
};

/* ─────────────  CONTACT  ───────────── */
const Contact = () => {
  const { company: COMPANY } = useSite();
  return (
  <section id="kontakt" className="relative py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-12" data-testid="contact-section">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex items-center gap-4 mb-6 sm:mb-8">
        <span className="font-mono text-xs text-[#a8a29e]">[ 07 ]</span>
        <div className="h-px w-16 bg-[#ea580c]" />
        <span className="font-mono text-xs text-[#ea580c] uppercase tracking-[0.2em]">Kontakt</span>
      </div>

      <h2 className="font-display text-4xl sm:text-6xl lg:text-9xl uppercase leading-[0.95] sm:leading-[0.9] mb-8 sm:mb-12">
        Zadzwoń. <span className="text-[#ea580c]">Napisz</span>.<br/>
        <span className="text-[#ea580c]">Przyjedź</span>.
      </h2>

      <p className="text-base sm:text-lg text-[#a8a29e] max-w-2xl mb-10 sm:mb-16">
        Jesteśmy dostępni 6 dni w tygodniu. Udzielimy porady, pomożemy dobrać produkt i wycenimy Twoje ogrodzenie.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#2a2a2e] border border-[#2a2a2e]">
        {/* Telefon */}
        <a href={`tel:+48${COMPANY.phones[0].num.replace(/\s/g,'')}`} className="bg-[#0a0a0b] p-5 sm:p-7 lg:p-8 hover:bg-[#131316] transition-colors group" data-testid="contact-phone-1">
          <Phone className="text-[#ea580c] mb-3 sm:mb-4 group-hover:scale-110 transition-transform" size={26}/>
          <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c] mb-1.5 sm:mb-2">Telefon — Jakub</div>
          <div className="font-display text-2xl sm:text-3xl text-white">{COMPANY.phones[0].num}</div>
        </a>
        <a href={`tel:+48${COMPANY.phones[1].num.replace(/\s/g,'')}`} className="bg-[#0a0a0b] p-5 sm:p-7 lg:p-8 hover:bg-[#131316] transition-colors group" data-testid="contact-phone-2">
          <Phone className="text-[#ea580c] mb-3 sm:mb-4 group-hover:scale-110 transition-transform" size={26}/>
          <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c] mb-1.5 sm:mb-2">Telefon — Piotr</div>
          <div className="font-display text-2xl sm:text-3xl text-white">{COMPANY.phones[1].num}</div>
        </a>
        <a href={`mailto:${COMPANY.email}`} className="bg-[#0a0a0b] p-5 sm:p-7 lg:p-8 hover:bg-[#131316] transition-colors group" data-testid="contact-email">
          <Mail className="text-[#ea580c] mb-3 sm:mb-4 group-hover:scale-110 transition-transform" size={26}/>
          <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c] mb-1.5 sm:mb-2">Email</div>
          <div className="text-base sm:text-xl text-white break-all">{COMPANY.email}</div>
        </a>
        <a href={COMPANY.mapsUrl} target="_blank" rel="noopener noreferrer" className="bg-[#0a0a0b] p-5 sm:p-7 lg:p-8 hover:bg-[#131316] transition-colors group" data-testid="contact-address">
          <MapPin className="text-[#ea580c] mb-3 sm:mb-4 group-hover:scale-110 transition-transform" size={26}/>
          <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c] mb-1.5 sm:mb-2">Adres</div>
          <div className="text-base sm:text-xl text-white leading-tight">{COMPANY.address}</div>
        </a>
      </div>

      <div className="mt-8 sm:mt-12 grid lg:grid-cols-2 gap-6 sm:gap-8">
        <div className="card p-6 sm:p-8" data-testid="hours-card">
          <Clock className="text-[#ea580c] mb-3 sm:mb-4" size={26}/>
          <h3 className="font-display text-xl sm:text-2xl uppercase text-white mb-3 sm:mb-4">Godziny otwarcia</h3>
          <div className="space-y-2 text-sm sm:text-base text-[#d6d3d1]">
            <div className="flex justify-between border-b border-[#2a2a2e] pb-2"><span>Poniedziałek – Piątek</span><span className="font-mono text-[#ea580c]">9:00 – 17:00</span></div>
            <div className="flex justify-between border-b border-[#2a2a2e] pb-2"><span>Sobota</span><span className="font-mono text-[#ea580c]">9:00 – 14:00</span></div>
            <div className="flex justify-between"><span>Niedziela</span><span className="font-mono text-[#a8a29e]">zamknięte</span></div>
          </div>
        </div>

        <div className="card p-6 sm:p-8 relative overflow-hidden" data-testid="cta-card">
          <div className="absolute inset-0 bg-fence opacity-10" />
          <div className="relative">
            <span className="tag tag-orange mb-4">// NAJSZYBCIEJ TELEFONICZNIE</span>
            <div className="mt-4 space-y-1">
              <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl uppercase text-white leading-tight">{COMPANY.phones[0].num} <span className="text-[#ea580c]">·</span> {COMPANY.phones[1].num}</h3>
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#a8a29e]">Jakub <span className="text-[#ea580c]">·</span> Piotr</div>
            </div>
            <div className="mt-5 sm:mt-6 flex flex-wrap gap-3">
              <a href={`tel:+48${COMPANY.phones[0].num.replace(/\s/g,'')}`} className="btn-primary" data-testid="cta-call-btn-1">
                <Phone size={16}/> Jakub
              </a>
              <a href={`tel:+48${COMPANY.phones[1].num.replace(/\s/g,'')}`} className="btn-ghost" data-testid="cta-call-btn-2">
                <Phone size={16}/> Piotr
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

/* ─────────────  FOOTER  ───────────── */
const Footer = () => {
  const { company: COMPANY } = useSite();
  return (
  <footer className="border-t border-[#2a2a2e] bg-[#0a0a0b] py-12 px-6 lg:px-12" data-testid="footer">
    <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 gap-8 items-start">
      <div>
        <Logo />
        <p className="text-sm text-[#a8a29e] mt-4 max-w-xs leading-relaxed">
          Producent siatki ogrodzeniowej i kompletnych systemów panelowych. Fajsławice, woj. lubelskie.
        </p>
        <p className="text-xs text-[#57534e] mt-4 font-mono">NIP {COMPANY.nip}</p>
      </div>
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c] mb-3">Nawigacja</div>
        <ul className="space-y-2 text-sm text-[#a8a29e]">
          <li><a href="#o-nas" className="hover:text-white">O firmie</a></li>
          <li><a href="#produkty" className="hover:text-white">Produkty</a></li>
          <li><a href="#cennik" className="hover:text-white">Cennik</a></li>
          <li><a href="#kontakt" className="hover:text-white">Kontakt</a></li>
        </ul>
      </div>
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c] mb-3">Kontakt</div>
        <ul className="space-y-2 text-sm text-[#d6d3d1]">
          <li><a href={`tel:+48${COMPANY.phones[0].num.replace(/\s/g,'')}`} className="hover:text-[#ea580c]">{COMPANY.phones[0].num} <span className="text-[#57534e]">· {COMPANY.phones[0].name}</span></a></li>
          <li><a href={`tel:+48${COMPANY.phones[1].num.replace(/\s/g,'')}`} className="hover:text-[#ea580c]">{COMPANY.phones[1].num} <span className="text-[#57534e]">· {COMPANY.phones[1].name}</span></a></li>
          <li><a href={`mailto:${COMPANY.email}`} className="hover:text-[#ea580c] break-all">{COMPANY.email}</a></li>
          <li className="text-[#a8a29e]">{COMPANY.address}</li>
        </ul>
      </div>
    </div>
    <div className="max-w-[1400px] mx-auto mt-12 pt-6 border-t border-[#2a2a2e] flex flex-col md:flex-row justify-between gap-4 text-xs text-[#57534e]">
      <div>© {new Date().getFullYear()} Ogrodzenia Fajsławice — {COMPANY.owner}. Wszelkie prawa zastrzeżone.</div>
      <div className="font-mono">Producent siatki ogrodzeniowej · Lubelskie</div>
    </div>
  </footer>
  );
};

/* ─────────────  APP  ───────────── */
import Gallery from "./Gallery";
import { AdminLogin, AdminDashboard } from "@/Admin";

/* ─────────────  STICKY MOBILE CTA  ───────────── */
const StickyMobileCTA = () => {
  const { company } = useSite();
  const phone = company?.phones?.[0]?.num || "785 405 785";
  const name = company?.phones?.[0]?.name || "Jakub";
  const telHref = `tel:+48${phone.replace(/\s/g, "")}`;
  return (
    <a
      href={telHref}
      data-testid="sticky-mobile-cta"
      className="lg:hidden fixed bottom-3 left-3 right-3 z-40 inline-flex items-center justify-center gap-3 px-5 py-3.5 bg-[#ea580c] hover:bg-[#c2410c] text-white font-mono text-sm uppercase tracking-widest shadow-2xl shadow-[#ea580c]/40 border border-[#ea580c]/40 backdrop-blur"
    >
      <Phone size={18} />
      Zadzwoń {name} · {phone}
    </a>
  );
};


/* ─────────────  PRODUCT PAGE (podstrona produktu)  ───────────── */
const HomePage = () => (
  <>
    <Hero />
    <About />
    <Products />
    <Gallery />
    <Cennik />
    <Configurator />
    <Location />
    <Transport />
    <Contact />
  </>
);

const ProductPage = () => {
  const { slug } = useParams();
  const { productPages, company, loading } = useSite();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    // Aktualizuj <title> i meta description per podstrona (SEO)
    const data = (productPages || {})[slug];
    if (data) {
      document.title = data.seoTitle || `${data.title} — Ogrodzenia Fajsławice`;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "description"); document.head.appendChild(meta); }
      meta.setAttribute("content", data.metaDescription || data.lead || "");
    }
    return () => { document.title = "Ogrodzenia Fajsławice — Siatki, Panele 3D, Bramy"; };
  }, [slug, productPages]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="font-mono text-[#a8a29e]">Ładowanie…</div></div>;
  const data = (productPages || {})[slug];
  if (!data) return <Navigate to="/" replace />;

  // Schema.org Product
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": data.title,
    "description": data.metaDescription || data.lead,
    "brand": { "@type": "Brand", "name": company?.name || "Ogrodzenia Fajsławice" },
    "manufacturer": {
      "@type": "Organization",
      "name": company?.name || "Ogrodzenia Fajsławice",
      "address": { "@type": "PostalAddress", "addressLocality": "Fajsławice", "addressRegion": "PL-LU", "postalCode": "21-060", "addressCountry": "PL" },
    },
  };

  return (
    <>
      <Nav />
      <main className="bg-[#0a0a0b] text-white min-h-screen" data-testid={`product-page-${slug}`}>
        {/* Breadcrumb */}
        <div className="border-b border-[#2a2a2e]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-4 font-mono text-[10px] sm:text-xs uppercase tracking-widest text-[#a8a29e]">
            <Link to="/" className="hover:text-[#ea580c]">Home</Link>
            <span className="mx-2 text-[#57534e]">›</span>
            <Link to="/#produkty" className="hover:text-[#ea580c]">Produkty</Link>
            <span className="mx-2 text-[#57534e]">›</span>
            <span className="text-white">{data.title}</span>
          </div>
        </div>

        {/* Hero */}
        <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12 border-b border-[#2a2a2e]">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <span className="font-mono text-xs text-[#ea580c]">// produkt</span>
              <div className="h-px flex-1 bg-[#2a2a2e]"/>
            </div>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl uppercase leading-[0.95] text-white mb-6">
              {data.title}
            </h1>
            <p className="text-base sm:text-xl text-[#d6d3d1] max-w-3xl leading-relaxed">{data.lead}</p>
          </div>
        </section>

        {/* Intro */}
        <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12">
          <div className="max-w-[1100px] mx-auto space-y-5 text-sm sm:text-base text-[#d6d3d1] leading-relaxed">
            {data.intro.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
          </div>
        </section>

        {/* Warianty — tabela 2D (rzędy × kolumny) */}
        {data.variantTable?.rows?.length > 0 && data.variantTable?.cols?.length > 0 && (
          <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12 bg-[#0d0d0f] border-y border-[#2a2a2e]">
            <div className="max-w-[1100px] mx-auto">
              <h2 className="font-display text-3xl sm:text-5xl uppercase text-white mb-8">Warianty <span className="text-[#ea580c]">do wyboru</span></h2>
              <div className="border border-[#2a2a2e] overflow-x-auto">
                <table className="w-full text-left border-collapse" data-testid="variants-table">
                  <thead className="bg-[#131316] border-b border-[#2a2a2e]">
                    <tr>
                      <th className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-[#a8a29e] px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap border-r border-[#2a2a2e]">
                        <span className="text-[#ea580c]">{data.variantTable.rowsLabel || ""}</span>
                        {data.variantTable.colsLabel && <span className="text-[#57534e]"> · {data.variantTable.colsLabel} →</span>}
                      </th>
                      {data.variantTable.cols.map((col, j) => (
                        <th key={j} className="font-display text-sm sm:text-base uppercase text-white px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap text-center">
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.variantTable.rows.map((row, i) => (
                      <tr key={i} className="border-t border-[#2a2a2e] hover:bg-[#131316]/40 transition-colors">
                        <td className="font-display text-sm sm:text-base uppercase text-white px-3 sm:px-5 py-3 sm:py-4 whitespace-nowrap border-r border-[#2a2a2e] bg-[#0a0a0b]/60">
                          {row}
                        </td>
                        {data.variantTable.cols.map((_, j) => (
                          <td key={j} className="px-3 sm:px-5 py-3 sm:py-4 text-center">
                            <span className="inline-block w-2 h-2 sm:w-2.5 sm:h-2.5 bg-[#ea580c] rounded-full" aria-label="Dostępne" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 font-mono text-[10px] sm:text-xs uppercase tracking-widest text-[#57534e]">
                <span className="inline-block w-2 h-2 bg-[#ea580c] rounded-full mr-2 align-middle"/> = wariant dostępny w naszej ofercie
              </p>
            </div>
          </section>
        )}

        {/* FAQ */}
        {data.faq?.length > 0 && (
          <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12 bg-[#0d0d0f] border-y border-[#2a2a2e]">
            <div className="max-w-[1000px] mx-auto">
              <h2 className="font-display text-3xl sm:text-5xl uppercase text-white mb-8">FAQ</h2>
              <div className="space-y-3">
                {data.faq.map((f, i) => (
                  <details key={i} className="group bg-[#0a0a0b] border border-[#2a2a2e] hover:border-[#ea580c]/40 transition-colors">
                    <summary className="cursor-pointer p-4 sm:p-5 flex justify-between items-start gap-4 text-sm sm:text-base text-white">
                      <span className="font-display uppercase tracking-tight">{f.q}</span>
                      <span className="text-[#ea580c] text-xl group-open:rotate-45 transition-transform shrink-0">+</span>
                    </summary>
                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 text-sm text-[#d6d3d1] leading-relaxed border-t border-[#2a2a2e] pt-3">{f.a}</div>
                  </details>
                ))}
              </div>
              <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": data.faq.map((f) => ({
                  "@type": "Question",
                  "name": f.q,
                  "acceptedAnswer": { "@type": "Answer", "text": f.a },
                })),
              }) }} />
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12">
          <div className="max-w-[1400px] mx-auto bg-[#0d0d0f] border border-[#ea580c]/30 p-8 sm:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-display text-3xl sm:text-5xl uppercase text-white mb-4">{data.ctaLabel || "Zapytaj o ofertę"}</h2>
                <p className="text-sm sm:text-base text-[#a8a29e]">Zadzwoń, odpowiemy na pytania i przygotujemy wycenę zwykle w tym samym dniu.</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href={`tel:+48${(company?.phones?.[0]?.num || "785 405 785").replace(/\s/g, "")}`} className="btn-primary">Zadzwoń: {company?.phones?.[0]?.num || "785 405 785"}</a>
                <Link to="/#cennik" className="btn-ghost">Sprawdź cennik</Link>
                {data.cennikTab === "panele" && <Link to="/#konfigurator" className="btn-ghost">Konfigurator</Link>}
              </div>
            </div>
          </div>
        </section>

        {/* Powiązane podstrony */}
        <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-12 border-t border-[#2a2a2e]">
          <div className="max-w-[1400px] mx-auto">
            <div className="font-mono text-xs uppercase tracking-widest text-[#ea580c] mb-4">// Zobacz też</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {Object.entries(productPages).filter(([s]) => s !== slug).map(([s, p]) => (
                <Link key={s} to={`/produkty/${s}`} className="block bg-[#0d0d0f] border border-[#2a2a2e] hover:border-[#ea580c] p-5 transition-colors group">
                  <div className="font-display text-lg uppercase text-white group-hover:text-[#ea580c] transition-colors">{p.title}</div>
                  <div className="text-xs text-[#a8a29e] mt-1.5 line-clamp-2">{p.lead}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
    </>
  );
};

function App() {
  return (
    <BrowserRouter>
      <SiteProvider>
        <Routes>
          <Route path="/" element={
            <div className="App min-h-screen bg-[#0a0a0b] text-white" data-testid="app-root">
              <Nav />
              <HomePage />
              <Footer />
              <StickyMobileCTA />
            </div>
          } />
          <Route path="/produkty/:slug" element={
            <div className="App min-h-screen bg-[#0a0a0b] text-white" data-testid="app-root">
              <ProductPage />
              <StickyMobileCTA />
            </div>
          } />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </SiteProvider>
    </BrowserRouter>
  );
}

export default App;
